<!--
  @component
  Provide an application’s shell that makes the web app more like a native app. It also handles the
  dark/light mode switching. This component has to be placed directly under `<body>` (or
  `<div style="display:contents">` in a SvelteKit app).
-->
<script>
  import { onMount } from 'svelte';

  /**
   * @import { Snippet } from 'svelte';
   */

  /**
   * @typedef {object} Props
   * @property {'horizontal' | 'vertical'} [orientation] Orientation of the app shell’s children.
   * @property {Snippet} [children] Primary slot content.
   */

  /**
   * @type {Props & Record<string, any>}
   */
  let {
    /* eslint-disable prefer-const */
    orientation = 'horizontal',
    children,
    ...restProps
    /* eslint-enable prefer-const */
  } = $props();

  const stylesheets = [
    // https://fonts.google.com/share?selection.family=Merriweather+Sans:ital,wght@0,300..800;1,300..800|Noto+Sans+Mono:wght@100..900
    'https://fonts.googleapis.com/css2?family=Merriweather+Sans:ital,wght@0,300..800;1,300..800&family=Noto+Sans+Mono:wght@100..900&display=swap',
    // https://fonts.google.com/icons?icon.set=Material+Symbols
    // Use `font-display: block;` @see https://stackoverflow.com/q/41710834
    'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=block',
  ];

  let fontLoaded = $state(false);

  onMount(() => {
    const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)');
    const { dataset } = document.documentElement;

    /**
     * Apply the Dark or Light theme.
     */
    const applyTheme = () => {
      if (dataset.autoTheming !== 'false') {
        dataset.theme = mediaQuery.matches ? 'dark' : 'light';
      }
    };

    applyTheme();
    mediaQuery.addEventListener('change', applyTheme);

    const fontTimer = globalThis.setTimeout(() => {
      fontLoaded = true;
    }, 1000);

    return () => {
      mediaQuery.removeEventListener('change', applyTheme);
      globalThis.clearTimeout(fontTimer);
    };
  });
</script>

<!--
  In the viewport meta tag, we set the maximum scale to 1 to prevent zooming in on mobile devices.
  @see https://stackoverflow.com/q/2989263
-->
<svelte:head>
  <meta
    name="viewport"
    content={[
      'width=device-width',
      'initial-scale=1',
      'maximum-scale=1',
      'interactive-widget=resizes-content',
    ].join(', ')}
  />
  <meta name="google" content="notranslate" />
  <link rel="preconnect" href="https://fonts.googleapis.com" />
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="anonymous" />
  {#each stylesheets as href (href)}
    <link rel="preload" {href} as="style" />
    <link rel="stylesheet" {href} />
  {/each}
</svelte:head>

{#if !fontLoaded}
  <!-- Preload fonts, including the icons -->
  <div class="font-loader" aria-hidden="true" style:opacity="0">
    Loading <strong>Sveltia</strong> <em>UI</em>
    <span role="none" class="material-symbols-outlined">favorite</span>
  </div>
{/if}

<div
  {...restProps}
  role="none"
  class="sui app-shell {orientation}"
  ondragover={(event) => event.preventDefault()}
  ondrop={(event) => event.preventDefault()}
  oncontextmenu={(event) => {
    // Allow context menu in developer mode
    if (document.documentElement.matches('[data-env="dev"]')) {
      return;
    }

    // eslint-disable-next-line prefer-destructuring
    const target = /** @type {HTMLElement} */ (event.target);

    // Allow context menu on text inputs and contentEditable elements
    if (
      document.documentElement.matches('[data-env="dev"]') ||
      (target?.matches('input, textarea') && 'maxLength' in target) ||
      /** @type {HTMLElement} */ (target?.closest('[role="textbox"]'))?.contentEditable === 'true'
    ) {
      return;
    }

    event.preventDefault();
  }}
>
  {@render children?.()}
</div>

<style>:global(:root),
:global(:host) {
  --sui-base-hue: 210;
  --sui-highlight-foreground-color: hsl(var(--sui-foreground-color-1-hsl));
  --sui-primary-foreground-color: hsl(var(--sui-foreground-color-2-hsl));
  --sui-secondary-foreground-color: hsl(var(--sui-foreground-color-3-hsl));
  --sui-tertiary-foreground-color: hsl(var(--sui-foreground-color-4-hsl));
  --sui-disabled-foreground-color: hsl(var(--sui-foreground-color-5-hsl));
  --sui-error-foreground-color: hsl(
    var(--sui-error-color-hue) var(--sui-alert-foreground-color-saturation)
      var(--sui-alert-foreground-color-lightness)
  );
  --sui-warning-foreground-color: hsl(
    var(--sui-warning-color-hue) var(--sui-alert-foreground-color-saturation)
      var(--sui-alert-foreground-color-lightness)
  );
  --sui-info-foreground-color: hsl(
    var(--sui-info-color-hue) var(--sui-alert-foreground-color-saturation)
      var(--sui-alert-foreground-color-lightness)
  );
  --sui-success-foreground-color: hsl(
    var(--sui-success-color-hue) var(--sui-alert-foreground-color-saturation)
      var(--sui-alert-foreground-color-lightness)
  );
  --sui-hover-background-color: hsl(var(--sui-background-color-5-hsl) / 35%);
  --sui-selected-background-color: hsl(var(--sui-background-color-5-hsl) / 75%);
  --sui-active-background-color: hsl(var(--sui-background-color-5-hsl) / 100%);
  --sui-content-background-color: hsl(var(--sui-background-color-1-hsl));
  --sui-code-background-color: hsl(var(--sui-background-color-3-hsl));
  --sui-primary-background-color: hsl(var(--sui-background-color-2-hsl));
  --sui-primary-background-color-translucent: hsl(var(--sui-background-color-2-hsl) / 80%);
  --sui-secondary-background-color: hsl(var(--sui-background-color-3-hsl));
  --sui-secondary-background-color-translucent: hsl(var(--sui-background-color-3-hsl) / 80%);
  --sui-tertiary-background-color: hsl(var(--sui-background-color-4-hsl));
  --sui-tertiary-background-color-translucent: hsl(var(--sui-background-color-4-hsl) / 80%);
  --sui-disabled-background-color: hsl(var(--sui-background-color-4-hsl));
  --sui-error-background-color: hsl(
    var(--sui-error-color-hue) var(--sui-alert-background-color-saturation)
      var(--sui-alert-background-color-lightness)
  );
  --sui-warning-background-color: hsl(
    var(--sui-warning-color-hue) var(--sui-alert-background-color-saturation)
      var(--sui-alert-background-color-lightness)
  );
  --sui-info-background-color: hsl(
    var(--sui-info-color-hue) var(--sui-alert-background-color-saturation)
      var(--sui-alert-background-color-lightness)
  );
  --sui-success-background-color: hsl(
    var(--sui-success-color-hue) var(--sui-alert-background-color-saturation)
      var(--sui-alert-background-color-lightness)
  );
  --sui-focus-ring-width: 4px;
  --sui-primary-border-color: hsl(var(--sui-border-color-2-hsl));
  --sui-secondary-border-color: hsl(var(--sui-border-color-3-hsl));
  --sui-error-border-color: hsl(
    var(--sui-error-color-hue) var(--sui-alert-border-color-saturation)
      var(--sui-alert-border-color-lightness)
  );
  --sui-warning-border-color: hsl(
    var(--sui-warning-color-hue) var(--sui-alert-border-color-saturation)
      var(--sui-alert-border-color-lightness)
  );
  --sui-info-border-color: hsl(
    var(--sui-info-color-hue) var(--sui-alert-border-color-saturation)
      var(--sui-alert-border-color-lightness)
  );
  --sui-success-border-color: hsl(
    var(--sui-success-color-hue) var(--sui-alert-border-color-saturation)
      var(--sui-alert-border-color-lightness)
  );
  --sui-popup-shadow-color: hsl(var(--sui-shadow-color) / 40%);
  --sui-popup-backdrop-color: hsl(var(--sui-shadow-color) / 40%);
  --sui-font-family-default: "Merriweather Sans", sans-serif;
  --sui-font-size-xxx-large: 24px;
  --sui-font-size-xx-large: 20px;
  --sui-font-size-x-large: 18px;
  --sui-font-size-large: 16px;
  --sui-font-size-default: 14px;
  --sui-font-size-small: 12px;
  --sui-font-size-x-small: 10px;
  --sui-font-weight-normal: 300;
  --sui-font-weight-bold: 600;
  --sui-font-family-monospace: "Noto Sans Mono", monospace;
  --sui-font-size-monospace: 0.95em;
  --sui-line-height-default: 1.25;
  --sui-line-height-compact: 1.5;
  --sui-line-height-comfortable: 1.75;
  --sui-word-spacing-normal: 1px;
  --sui-heading-margin: 0;
  --sui-heading-font-family: var(--sui-font-family-default);
  --sui-heading-font-weight: var(--sui-font-weight-bold);
  --sui-heading-line-height: var(--sui-line-height-default);
  --sui-paragraph-margin: 1.75em;
  --sui-control-small-border-width: 1px;
  --sui-control-small-border-radius: calc(var(--sui-control-small-height) / 8);
  --sui-control-small-padding: 0 calc((var(--sui-control-small-height) / 5));
  --sui-control-small-height: 24px;
  --sui-control-medium-border-width: 1px;
  --sui-control-medium-border-radius: calc(var(--sui-control-medium-height) / 8);
  --sui-control-medium-padding: 0 calc((var(--sui-control-medium-height) / 4));
  --sui-control-medium-height: 32px;
  --sui-control-large-border-width: 1px;
  --sui-control-large-border-radius: calc(var(--sui-control-large-height) / 8);
  --sui-control-large-padding: 0 calc((var(--sui-control-large-height) / 3));
  --sui-control-large-height: 40px;
  --sui-control-border-color: hsl(var(--sui-border-color-2-hsl));
  --sui-control-foreground-color: var(--sui-primary-foreground-color);
  --sui-control-background-color: hsl(var(--sui-background-color-4-hsl));
  --sui-control-font-family: var(--sui-font-family-default);
  --sui-control-font-size: var(--sui-font-size-default);
  --sui-control-line-height: var(--sui-line-height-compact);
  --sui-button-small-border-radius: var(--sui-control-small-border-radius);
  --sui-button-small-padding: var(--sui-control-small-padding);
  --sui-button-small-height: var(--sui-control-small-height);
  --sui-button-medium-border-radius: var(--sui-control-medium-border-radius);
  --sui-button-medium-padding: var(--sui-control-medium-padding);
  --sui-button-medium-height: var(--sui-control-medium-height);
  --sui-button-large-border-radius: var(--sui-control-large-border-radius);
  --sui-button-large-padding: var(--sui-control-large-padding);
  --sui-button-large-height: var(--sui-control-large-height);
  --sui-button-border-color: var(--sui-control-border-color);
  --sui-button-background-color: var(--sui-control-background-color);
  --sui-checkbox-border-radius: var(--sui-control-medium-border-radius);
  --sui-checkbox-height: 20px;
  --sui-checkbox-border-color: hsl(var(--sui-border-color-1-hsl));
  --sui-checkbox-background-color: var(--sui-control-background-color);
  --sui-option-border-radius: var(--sui-control-medium-border-radius);
  --sui-option-padding: calc((var(--sui-control-medium-height) / 6))
    calc((var(--sui-control-medium-height) / 5));
  --sui-option-height: var(--sui-control-medium-height);
  --sui-listbox-border-radius: var(--sui-control-medium-border-radius);
  --sui-listbox-border-color: var(--sui-control-border-color);
  --sui-listbox-foreground-color: var(--sui-control-foreground-color);
  --sui-listbox-background-color: hsl(var(--sui-background-color-1-hsl));
  --sui-textbox-border-radius: var(--sui-control-medium-border-radius);
  --sui-textbox-height: var(--sui-control-medium-height);
  --sui-textbox-border-color: var(--sui-control-border-color);
  --sui-textbox-foreground-color: var(--sui-control-foreground-color);
  --sui-textbox-background-color: hsl(var(--sui-background-color-1-hsl));
  --sui-textbox-font-family: var(--sui-font-family-default);
  --sui-textbox-font-size: var(--sui-font-size-default);
  --sui-textbox-singleline-padding: 0 8px;
  --sui-textbox-singleline-min-width: 240px;
  --sui-textbox-singleline-line-height: var(--sui-line-height-compact);
  --sui-textbox-multiline-padding: 16px;
  --sui-textbox-multiline-min-width: 480px;
  --sui-textbox-multiline-line-height: var(--sui-line-height-comfortable);
  --sui-tab-height: var(--sui-control-medium-height);
  --sui-tab-small-height: var(--sui-control-small-height);
  --sui-tab-medium-height: var(--sui-control-medium-height);
  --sui-tab-large-height: var(--sui-control-large-height);
  --sui-primary-toolbar-size: 56px;
  --sui-secondary-toolbar-size: 48px;
  --sui-bottom-navigation-height: var(--sui-primary-toolbar-size);
  --sui-primary-row-height: 56px;
  --sui-secondary-row-height: 40px;
}
@media (pointer: coarse) {
  :global(:root),
  :global(:host) {
    --sui-control-small-height: 32px;
    --sui-control-medium-height: 40px;
    --sui-control-large-height: 48px;
    --sui-checkbox-height: 24px;
    --sui-secondary-row-height: 48px;
  }
}
@media (prefers-reduced-transparency) {
  :global(:root),
  :global(:host) {
    --sui-primary-background-color-translucent: hsl(var(--sui-background-color-2-hsl));
    --sui-secondary-background-color-translucent: hsl(var(--sui-background-color-3-hsl));
    --sui-tertiary-background-color-translucent: hsl(var(--sui-background-color-4-hsl));
  }
}
:global(:root[data-theme=light]),
:global(:host[data-theme=light]) {
  color-scheme: light;
  --sui-foreground-color-1-hsl: var(--sui-base-hue) 5% 5%;
  --sui-foreground-color-2-hsl: var(--sui-base-hue) 5% 25%;
  --sui-foreground-color-3-hsl: var(--sui-base-hue) 5% 35%;
  --sui-foreground-color-4-hsl: var(--sui-base-hue) 5% 45%;
  --sui-foreground-color-5-hsl: var(--sui-base-hue) 5% 65%;
  --sui-background-color-1-hsl: var(--sui-base-hue) 5% 100%;
  --sui-background-color-2-hsl: var(--sui-base-hue) 5% 98%;
  --sui-background-color-3-hsl: var(--sui-base-hue) 5% 95%;
  --sui-background-color-4-hsl: var(--sui-base-hue) 5% 92%;
  --sui-background-color-5-hsl: var(--sui-base-hue) 5% 84%;
  --sui-border-color-1-hsl: var(--sui-base-hue) 5% 60%;
  --sui-border-color-2-hsl: var(--sui-base-hue) 5% 86%;
  --sui-border-color-3-hsl: var(--sui-base-hue) 5% 90%;
  --sui-shadow-color: var(--sui-base-hue) 10% 0%;
  --sui-primary-accent-color-text: hsl(var(--sui-base-hue) 80% 40%);
  --sui-primary-accent-color-light: hsl(var(--sui-base-hue) 80% 45%);
  --sui-primary-accent-color: hsl(var(--sui-base-hue) 80% 40%);
  --sui-primary-accent-color-dark: hsl(var(--sui-base-hue) 80% 35%);
  --sui-primary-accent-color-inverted: hsl(var(--sui-base-hue) 10% 100%);
  --sui-primary-accent-color-translucent: hsl(var(--sui-base-hue) 80% 50% / 40%);
  --sui-error-color-hue: 0;
  --sui-warning-color-hue: 40;
  --sui-info-color-hue: 210;
  --sui-success-color-hue: 100;
  --sui-alert-foreground-color-saturation: 85%;
  --sui-alert-foreground-color-lightness: 25%;
  --sui-alert-background-color-saturation: 65%;
  --sui-alert-background-color-lightness: 90%;
  --sui-alert-border-color-saturation: 48%;
  --sui-alert-border-color-lightness: 68%;
}
:global(:root[data-theme=dark]),
:global(:host[data-theme=dark]) {
  color-scheme: dark;
  --sui-foreground-color-1-hsl: var(--sui-base-hue) 10% 95%;
  --sui-foreground-color-2-hsl: var(--sui-base-hue) 10% 75%;
  --sui-foreground-color-3-hsl: var(--sui-base-hue) 10% 65%;
  --sui-foreground-color-4-hsl: var(--sui-base-hue) 10% 55%;
  --sui-foreground-color-5-hsl: var(--sui-base-hue) 10% 35%;
  --sui-background-color-1-hsl: var(--sui-base-hue) 10% 8%;
  --sui-background-color-2-hsl: var(--sui-base-hue) 10% 10%;
  --sui-background-color-3-hsl: var(--sui-base-hue) 10% 13%;
  --sui-background-color-4-hsl: var(--sui-base-hue) 10% 16%;
  --sui-background-color-5-hsl: var(--sui-base-hue) 10% 26%;
  --sui-border-color-1-hsl: var(--sui-base-hue) 10% 40%;
  --sui-border-color-2-hsl: var(--sui-base-hue) 10% 24%;
  --sui-border-color-3-hsl: var(--sui-base-hue) 10% 20%;
  --sui-shadow-color: var(--sui-base-hue) 10% 0%;
  --sui-primary-accent-color-text: hsl(var(--sui-base-hue) 100% 60%);
  --sui-primary-accent-color-light: hsl(var(--sui-base-hue) 100% 45%);
  --sui-primary-accent-color: hsl(var(--sui-base-hue) 100% 40%);
  --sui-primary-accent-color-dark: hsl(var(--sui-base-hue) 100% 35%);
  --sui-primary-accent-color-inverted: hsl(var(--sui-base-hue) 10% 100%);
  --sui-primary-accent-color-translucent: hsl(var(--sui-base-hue) 80% 50% / 40%);
  --sui-error-color-hue: 0;
  --sui-warning-color-hue: 40;
  --sui-info-color-hue: 210;
  --sui-success-color-hue: 100;
  --sui-alert-foreground-color-saturation: 85%;
  --sui-alert-foreground-color-lightness: 75%;
  --sui-alert-background-color-saturation: 40%;
  --sui-alert-background-color-lightness: 10%;
  --sui-alert-border-color-saturation: 48%;
  --sui-alert-border-color-lightness: 38%;
}
@media (prefers-color-scheme: light) {
  :global(:root:not([data-theme])),
  :global(:host:not([data-theme])) {
    color-scheme: light;
    --sui-foreground-color-1-hsl: var(--sui-base-hue) 5% 5%;
    --sui-foreground-color-2-hsl: var(--sui-base-hue) 5% 25%;
    --sui-foreground-color-3-hsl: var(--sui-base-hue) 5% 35%;
    --sui-foreground-color-4-hsl: var(--sui-base-hue) 5% 45%;
    --sui-foreground-color-5-hsl: var(--sui-base-hue) 5% 65%;
    --sui-background-color-1-hsl: var(--sui-base-hue) 5% 100%;
    --sui-background-color-2-hsl: var(--sui-base-hue) 5% 98%;
    --sui-background-color-3-hsl: var(--sui-base-hue) 5% 95%;
    --sui-background-color-4-hsl: var(--sui-base-hue) 5% 92%;
    --sui-background-color-5-hsl: var(--sui-base-hue) 5% 84%;
    --sui-border-color-1-hsl: var(--sui-base-hue) 5% 60%;
    --sui-border-color-2-hsl: var(--sui-base-hue) 5% 86%;
    --sui-border-color-3-hsl: var(--sui-base-hue) 5% 90%;
    --sui-shadow-color: var(--sui-base-hue) 10% 0%;
    --sui-primary-accent-color-text: hsl(var(--sui-base-hue) 80% 40%);
    --sui-primary-accent-color-light: hsl(var(--sui-base-hue) 80% 45%);
    --sui-primary-accent-color: hsl(var(--sui-base-hue) 80% 40%);
    --sui-primary-accent-color-dark: hsl(var(--sui-base-hue) 80% 35%);
    --sui-primary-accent-color-inverted: hsl(var(--sui-base-hue) 10% 100%);
    --sui-primary-accent-color-translucent: hsl(var(--sui-base-hue) 80% 50% / 40%);
    --sui-error-color-hue: 0;
    --sui-warning-color-hue: 40;
    --sui-info-color-hue: 210;
    --sui-success-color-hue: 100;
    --sui-alert-foreground-color-saturation: 85%;
    --sui-alert-foreground-color-lightness: 25%;
    --sui-alert-background-color-saturation: 65%;
    --sui-alert-background-color-lightness: 90%;
    --sui-alert-border-color-saturation: 48%;
    --sui-alert-border-color-lightness: 68%;
  }
}
@media (prefers-color-scheme: dark) {
  :global(:root:not([data-theme])),
  :global(:host:not([data-theme])) {
    color-scheme: dark;
    --sui-foreground-color-1-hsl: var(--sui-base-hue) 10% 95%;
    --sui-foreground-color-2-hsl: var(--sui-base-hue) 10% 75%;
    --sui-foreground-color-3-hsl: var(--sui-base-hue) 10% 65%;
    --sui-foreground-color-4-hsl: var(--sui-base-hue) 10% 55%;
    --sui-foreground-color-5-hsl: var(--sui-base-hue) 10% 35%;
    --sui-background-color-1-hsl: var(--sui-base-hue) 10% 8%;
    --sui-background-color-2-hsl: var(--sui-base-hue) 10% 10%;
    --sui-background-color-3-hsl: var(--sui-base-hue) 10% 13%;
    --sui-background-color-4-hsl: var(--sui-base-hue) 10% 16%;
    --sui-background-color-5-hsl: var(--sui-base-hue) 10% 26%;
    --sui-border-color-1-hsl: var(--sui-base-hue) 10% 40%;
    --sui-border-color-2-hsl: var(--sui-base-hue) 10% 24%;
    --sui-border-color-3-hsl: var(--sui-base-hue) 10% 20%;
    --sui-shadow-color: var(--sui-base-hue) 10% 0%;
    --sui-primary-accent-color-text: hsl(var(--sui-base-hue) 100% 60%);
    --sui-primary-accent-color-light: hsl(var(--sui-base-hue) 100% 45%);
    --sui-primary-accent-color: hsl(var(--sui-base-hue) 100% 40%);
    --sui-primary-accent-color-dark: hsl(var(--sui-base-hue) 100% 35%);
    --sui-primary-accent-color-inverted: hsl(var(--sui-base-hue) 10% 100%);
    --sui-primary-accent-color-translucent: hsl(var(--sui-base-hue) 80% 50% / 40%);
    --sui-error-color-hue: 0;
    --sui-warning-color-hue: 40;
    --sui-info-color-hue: 210;
    --sui-success-color-hue: 100;
    --sui-alert-foreground-color-saturation: 85%;
    --sui-alert-foreground-color-lightness: 75%;
    --sui-alert-background-color-saturation: 40%;
    --sui-alert-background-color-lightness: 10%;
    --sui-alert-border-color-saturation: 48%;
    --sui-alert-border-color-lightness: 38%;
  }
}

:global(.material-symbols-outlined) {
  font-variation-settings: "FILL" 0, "wght" 300, "GRAD" 0, "opsz" 24;
}
:global(*),
:global(::before),
:global(::after) {
  overflow-anchor: none;
  scroll-behavior: smooth;
  box-sizing: border-box;
  outline-offset: 0px;
  outline-width: var(--sui-focus-ring-width) !important;
  outline-style: solid;
  outline-color: transparent;
  border-width: 0;
  border-style: solid;
  vertical-align: top;
}
@media (prefers-reduced-motion) {
  :global(*),
  :global(::before),
  :global(::after) {
    scroll-behavior: auto;
    transition-duration: 1ms !important;
  }
}
:global(::selection) {
  background-color: var(--sui-primary-accent-color-translucent);
}
:global(*) {
  -webkit-tap-highlight-color: transparent;
}
:global(:focus) {
  z-index: 1;
  outline-width: 0;
}
:global(:focus-visible) {
  outline-color: var(--sui-primary-accent-color-translucent);
  z-index: 2;
}
:global(h1) {
  margin: var(--sui-h1-margin, var(--sui-heading-margin));
  font-size: var(--sui-h1-font-size, 32px);
  font-family: var(--sui-h1-font-family, var(--sui-heading-font-family));
  font-weight: var(--sui-h1-font-weight, var(--sui-heading-font-weight));
  line-height: var(--sui-h1-line-height, var(--sui-heading-line-height));
}
:global(h2) {
  margin: var(--sui-h2-margin, var(--sui-heading-margin));
  font-size: var(--sui-h2-font-size, 28px);
  font-family: var(--sui-h2-font-family, var(--sui-heading-font-family));
  font-weight: var(--sui-h2-font-weight, var(--sui-heading-font-weight));
  line-height: var(--sui-h2-line-height, var(--sui-heading-line-height));
}
:global(h3) {
  margin: var(--sui-h3-margin, var(--sui-heading-margin));
  font-size: var(--sui-h3-font-size, 24px);
  font-family: var(--sui-h3-font-family, var(--sui-heading-font-family));
  font-weight: var(--sui-h3-font-weight, var(--sui-heading-font-weight));
  line-height: var(--sui-h3-line-height, var(--sui-heading-line-height));
}
:global(h4) {
  margin: var(--sui-h4-margin, var(--sui-heading-margin));
  font-size: var(--sui-h4-font-size, 20px);
  font-family: var(--sui-h4-font-family, var(--sui-heading-font-family));
  font-weight: var(--sui-h4-font-weight, var(--sui-heading-font-weight));
  line-height: var(--sui-h4-line-height, var(--sui-heading-line-height));
}
:global(h5) {
  margin: var(--sui-h5-margin, var(--sui-heading-margin));
  font-size: var(--sui-h5-font-size, 16px);
  font-family: var(--sui-h5-font-family, var(--sui-heading-font-family));
  font-weight: var(--sui-h5-font-weight, var(--sui-heading-font-weight));
  line-height: var(--sui-h5-line-height, var(--sui-heading-line-height));
}
:global(h6) {
  margin: var(--sui-h6-margin, var(--sui-heading-margin));
  font-size: var(--sui-h6-font-size, 12px);
  font-family: var(--sui-h6-font-family, var(--sui-heading-font-family));
  font-weight: var(--sui-h6-font-weight, var(--sui-heading-font-weight));
  line-height: var(--sui-h6-line-height, var(--sui-heading-line-height));
}
:global(strong) {
  font-weight: var(--sui-font-weight-bold, bold);
}
:global(a) {
  color: var(--sui-primary-accent-color-text);
  text-decoration: none;
  text-underline-offset: 2px;
}
:global(a:is(:hover, :focus, :active)) {
  text-decoration: underline;
}
:is(:root, :host)[data-underline-links=true] :global(a) {
  text-decoration: underline;
}
:global(:is(p, ul, ol, dl)) {
  margin: var(--sui-paragraph-margin) 0;
  line-height: var(--sui-line-height-comfortable);
}
:global(:is(ul, ol)) {
  padding-inline: 2em;
}
:global(:is(code, pre)) {
  border-radius: 4px;
  background-color: var(--sui-code-background-color);
  font-family: var(--sui-font-family-monospace);
  font-size: var(--sui-font-size-monospace);
  vertical-align: -0.05em;
}
:global(pre) {
  padding: 8px;
  line-height: var(--sui-line-height-compact);
  -webkit-user-select: text;
  user-select: text;
}
:global(code) {
  padding: 2px 4px;
}
:global(table) {
  border-collapse: collapse;
}
:global(:is(th, td)) {
  border: 1px solid var(--sui-textbox-border-color);
  padding: 8px;
}
:global(blockquote) {
  margin-inline: 16px 0;
  border-inline-start: 4px solid var(--sui-textbox-border-color);
  padding-inline-start: 12px;
}
:global(:is(.disabled, .readonly, [aria-disabled=true], [aria-readonly=true], [inert]):not(body)) {
  cursor: default;
  pointer-events: none;
  -webkit-user-select: none;
  user-select: none;
  filter: grayscale(1) opacity(0.35);
}
:global(:is(.disabled, .readonly, [aria-disabled=true], [aria-readonly=true], [inert]):not(body)) :global(*) {
  filter: grayscale(0) opacity(1);
}
:global(:is(.disabled, .readonly, [aria-disabled=true], [aria-readonly=true], [inert])) :global(*) {
  cursor: default;
  pointer-events: none;
  -webkit-user-select: none;
  user-select: none;
}

.font-loader {
  position: absolute;
  inset-inline-start: -99999px;
  font-family: var(--sui-font-family-default);
}

.app-shell {
  position: fixed;
  inset: 0;
  overflow: hidden;
  width: 100%;
  height: 100%;
  color: var(--sui-primary-foreground-color);
  background-color: var(--sui-primary-background-color);
  font-family: var(--sui-font-family-default);
  font-size: var(--sui-font-size-default);
  font-weight: var(--sui-font-weight-normal, normal);
  word-spacing: var(--sui-word-spacing-normal);
  -webkit-user-select: none;
  user-select: none;
  touch-action: none;
  cursor: default;
}
.app-shell.horizontal {
  display: flex;
  flex-direction: row;
  overflow: hidden;
}
.app-shell.vertical {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}</style>
